import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, Typography, Grid, TextField, Autocomplete } from '@mui/material';

// Define the shape of the form data to help with type inference
interface EmployeeDetailsForm {
  personal: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    gender: string;
    address1: string;
    address2: string;
    suburb: string;
    state: string;
    postcode: string;
  };
  // Add other sections if needed
}

const PersonalTab: React.FC = () => {
  const {
    control,
    formState: { errors }
  } = useFormContext<EmployeeDetailsForm>();

  // Dropdown options
  const genderOptions = ['Male', 'Female', 'Prefer not to say'];
  const stateOptions = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

  return (
    <Box component="div" sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.1rem' } }}
      >
        Information
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="personal.firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="First name"
                size="small"
                error={!!errors.personal?.firstName}
                helperText={(errors.personal?.firstName?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="personal.lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Last name"
                size="small"
                error={!!errors.personal?.lastName}
                helperText={(errors.personal?.lastName?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="personal.email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email address"
                size="small"
                error={!!errors.personal?.email}
                helperText={(errors.personal?.email?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="personal.phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Phone number"
                size="small"
                error={!!errors.personal?.phone}
                helperText={(errors.personal?.phone?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="personal.gender"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={genderOptions}
                value={field.value}
                onChange={(_, value) => field.onChange(value ?? '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Gender"
                    size="small"
                    error={!!errors.personal?.gender}
                    helperText={(errors.personal?.gender?.message as string) || ''}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{ mt: 3, mb: 2, fontSize: { xs: '1rem', sm: '1.1rem' } }}
      >
        Residential address
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="personal.address1"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address 1"
                size="small"
                error={!!errors.personal?.address1}
                helperText={(errors.personal?.address1?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="personal.address2"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address 2"
                size="small"
                error={!!errors.personal?.address2}
                helperText={(errors.personal?.address2?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="personal.suburb"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Suburb"
                size="small"
                error={!!errors.personal?.suburb}
                helperText={(errors.personal?.suburb?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name="personal.state"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={stateOptions}
                value={field.value}
                onChange={(_, value) => field.onChange(value ?? '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    size="small"
                    error={!!errors.personal?.state}
                    helperText={(errors.personal?.state?.message as string) || ''}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name="personal.postcode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Postcode"
                size="small"
                error={!!errors.personal?.postcode}
                helperText={(errors.personal?.postcode?.message as string) || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PersonalTab;
